import { Box, Button, IconButton, Stack, Tooltip } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { GLItemsDTO } from '../../../DTO/GLItemsDTO'
import LogChatCreateMsg from './LogChatCreateMsg'
import LogChatMsg from './LogChatMsg'
import { InfinitScroll } from '../../../services/InfinitScroll'
import { GLItemKind, GLItemStatus } from '../../../DTO/GLItemDTO'
import { AccessAlarm, UnfoldLess, UnfoldMore, Person } from '@mui/icons-material'

import './LogChat.scss'

enum Status {
  loading,
  loadMore,
  idle,
}

export default function LogChat({
  id,
  personId,
  customerId,
  disableNewMsg,
  needle,
  kind,
  statusKind,
  onClickDetail,
  initDisplaySize = 'minItems',
  markedId = -1,
  scrollToId = -1,
  userId = -1,
  pastduedate = false,
  onAlarmButton,
  onPersonButton,
}: {
  id?: number
  personId?: number
  customerId?: number
  disableNewMsg?: boolean
  needle?: string
  kind?: GLItemKind
  statusKind?: GLItemStatus
  onClickDetail?: (id: number) => void
  initDisplaySize?: 'minItems' | 'normalItems'
  markedId?: number
  scrollToId?: number
  userId?: number
  pastduedate?: boolean
  onAlarmButton?: () => void
  onPersonButton?: () => void
}) {
  const [status, setStatus] = useState(Status.loading)
  const [expandAll, setExpandAll] = useState<boolean | undefined>(undefined)
  const [localMarkedId, setLocalMarkedId] = useState(markedId)
  const [glitems] = useState(new GLItemsDTO({
    id: id && id > -1 ? id : undefined,
    page: 1,
    limit: 20,
    personId,
    customerId,
    needle,
    kind,
    status: statusKind,
    userId,
    pastduedate,
  }))
  const [listVersion, setListVersion] = useState(0)

  useEffect(() => {
    setLocalMarkedId(markedId)
  }, [markedId])

  useEffect(() => {
    setStatus(Status.loading)
    glitems.patchRequest({
      personId,
      customerId,
      kind,
      status: statusKind,
      userId: userId,
      pastduedate: pastduedate,
    })
    glitems.getFresh().then(() => {
      setStatus(Status.idle)
      setListVersion(v => v + 1)
    })
  }, [personId, customerId, kind, statusKind, userId, pastduedate])

  useEffect(() => {
    setStatus(Status.loading)
    glitems.setNeedle(needle || '').then(() => {
      setStatus(Status.idle)
      setListVersion(v => v + 1)
    })
  }, [needle])

  useEffect(() => {
    if (scrollToId < 0 || status === Status.loading) {
      return
    }
    let cancelled = false
    async function ensureVisible() {
      const maxExtraPages = 15
      for (let i = 0; i < maxExtraPages; i++) {
        if (cancelled) {
          return
        }
        const el = document.querySelector(`[data-glitem-id="${scrollToId}"]`)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
          return
        }
        if (!glitems.allowInfinitScroll) {
          return
        }
        setStatus(Status.loadMore)
        await glitems.getNextPage()
        setStatus(Status.idle)
        setListVersion(v => v + 1)
      }
    }
    ensureVisible()
    return () => { cancelled = true }
  }, [scrollToId, listVersion, status])

  async function refresh() {
    setStatus(Status.loading)
    await glitems.getFresh()
    setStatus(Status.idle)
    setListVersion(v => v + 1)
  }

  const [infinitScroll] = useState(new InfinitScroll({
    ref: useRef(null),
    cb: async () => {
      if (!glitems.allowInfinitScroll) { return }
      setStatus(Status.loadMore)
      await glitems.getNextPage()
      setStatus(Status.idle)
      setListVersion(v => v + 1)
    },
    saveScrollPositionCb: () => {},
    page: 1,
  }))

  function defaultExpandedFor(itemId: number): boolean {
    if (initDisplaySize === 'normalItems' && id && id > -1) {
      return itemId === id
    }
    if (itemId === -1) {
      return true
    }
    return false
  }

  return (
    <Box
      ref={infinitScroll.getRef()}
      className="LogChat scrollContent"
    >
      {(id && id > -1) && (
        <Box sx={{ marginBottom: 1 }}>
          <h3>{glitems.getHeadline1()}</h3>
          <h4>{glitems.getHeadline2()}</h4>
        </Box>
      )}

      <Stack
        className="LogChat-toolbar"
        direction="row"
        spacing={1}
        useFlexGap
        sx={{ justifyContent: 'flex-end', flexWrap: 'wrap', alignItems: 'center' }}
      >
        {onAlarmButton && (
          <Tooltip title="Überfällige Erinnerungen">
            <IconButton
              onClick={onAlarmButton}
              color={pastduedate ? 'primary' : 'default'}
              sx={{ minWidth: 40, minHeight: 40 }}
            >
              <AccessAlarm />
            </IconButton>
          </Tooltip>
        )}
        {onPersonButton && (
          <Tooltip title="Nur meine Einträge">
            <IconButton
              onClick={onPersonButton}
              color={userId > -1 ? 'primary' : 'default'}
              sx={{ minWidth: 40, minHeight: 40 }}
            >
              <Person />
            </IconButton>
          </Tooltip>
        )}
        <Button
          size="small"
          startIcon={expandAll === true ? <UnfoldLess /> : <UnfoldMore />}
          onClick={() => setExpandAll(prev => prev === true ? false : true)}
        >
          {expandAll === true ? 'Alle zuklappen' : 'Alle aufklappen'}
        </Button>
      </Stack>

      {!disableNewMsg && (
        <LogChatCreateMsg
          saveCB={refresh}
          personId={personId}
          id={id}
        />
      )}

      {status === Status.loading && <Box>Lade...</Box>}
      {status !== Status.loading && glitems.items.length === 0 && <Box>Keine Einträge</Box>}

      <div className="LogChat-list">
        {status !== Status.loading && glitems.get().map(item => (
          <LogChatMsg
            key={item.id}
            saveCB={refresh}
            item={item}
            onClickDetail={onClickDetail}
            marked={item.id === localMarkedId}
            defaultExpanded={defaultExpandedFor(item.id)}
            expandAll={expandAll}
          />
        ))}
      </div>
    </Box>
  )
}
